import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Paperclip, CheckCheck, XCircle } from "lucide-react";
import { Money } from "./Money";
import { ReceiptViewer } from "./ReceiptViewer";
import { BillShareButton } from "./BillShareButton";

const CARD = "rounded-2xl border border-border/60 bg-background/70 shadow-neu-sm";

// صفّ موافقة قابل للتوسّع — يعرض تفاصيل الفاتورة (بنود/ضريبة/مرفق) قبل الاعتماد.
export const BillApprovalRow = ({ bill, branchName, supplierName, accountName,
                                   onAction, actionKey, actionLabel, onReject, showShare = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const total = Number(bill.total || 0);
  const tax = Number(bill.tax || 0);
  const net = total - tax;
  const Cell = ({ label, val }) => (
    <div className="grid gap-0.5">
      <span className="text-[10px] font-bold uppercase text-muted-foreground">{label}</span>
      <span className="text-xs font-black">{val}</span>
    </div>
  );
  return (
    <div className={`${CARD} mb-2 overflow-hidden`} data-testid={`approval-card-${bill.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
              data-testid={`approval-toggle-${bill.id}`}
              className="flex w-full flex-wrap items-center gap-3 p-3 text-start hover:bg-primary/5">
        {open ? <ChevronUp className="h-4 w-4 text-primary" />
              : <ChevronDown className="h-4 w-4 text-primary" />}
        <span className="font-mono text-xs text-primary" dir="ltr">{bill.supplier_invoice_no || "—"}</span>
        <span className="min-w-0 flex-1 truncate text-xs font-semibold">{supplierName || bill.description || "—"}</span>
        <span className="text-[11px] text-muted-foreground">{branchName || "—"}</span>
        <Money value={total} currency={bill.currency} className="text-xs font-black" />
      </button>
      {open ? (
        <div className="border-t border-border/50 p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Cell label={t("acct.bills.details.category")} val={accountName || bill.expense_account} />
            <Cell label={t("acct.bills.details.date")}     val={bill.date} />
            <Cell label={t("acct.bills.details.due")}      val={bill.due_date || "—"} />
            <Cell label={t("acct.bills.details.net")}      val={<Money value={net} currency={bill.currency} />} />
            <Cell label={t("acct.bills.details.tax")}      val={<Money value={tax} currency={bill.currency} />} />
            <Cell label={t("acct.bills.details.total")}    val={<Money value={total} currency={bill.currency} className="text-primary" />} />
          </div>
          {bill.description ? (
            <p className="mt-3 rounded-xl bg-muted/60 p-3 text-xs" data-testid={`approval-desc-${bill.id}`}>
              {bill.description}
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            {bill.attachment_url ? (
              <button type="button" onClick={() => setShowAttach(true)}
                      data-testid={`approval-attach-${bill.id}`}
                      className="flex items-center gap-2 rounded-2xl border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/15">
                <Paperclip className="h-4 w-4" /> {t("acct.bills.details.attach")}
              </button>
            ) : <span className="text-[11px] text-muted-foreground">{t("acct.bills.details.noAttach")}</span>}
            <div className="flex flex-wrap items-center gap-2">
              {showShare ? <BillShareButton bill={bill} branchName={branchName} /> : null}
              {onReject ? (
                <button type="button" onClick={() => onReject(bill)} data-testid={`reject-btn-${bill.id}`}
                        className="flex items-center gap-2 rounded-2xl bg-rose-500 px-4 py-2 text-xs font-black text-white shadow-md hover:bg-rose-600">
                  <XCircle className="h-4 w-4" /> {t("acct.bills.approve.reject")}
                </button>
              ) : null}
              <button type="button" onClick={() => onAction(bill)} data-testid={`${actionKey}-btn-${bill.id}`}
                      className="flex items-center gap-2 rounded-2xl bg-blue-700 px-4 py-2 text-xs font-black text-white shadow-md hover:bg-blue-800">
                <CheckCheck className="h-4 w-4" /> {actionLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showAttach ? <ReceiptViewer url={bill.attachment_url} onClose={() => setShowAttach(false)} /> : null}
    </div>
  );
};
